import React, { useState } from 'react';
import { useSignUp } from '@clerk/clerk-react';
import { logger } from '../log';
import styles from './Auth.module.css';

const log = logger('auth');

interface CustomSignUpProps {
  onSignInClick: () => void;
}

type OAuthProvider = 'oauth_google' | 'oauth_github' | 'oauth_apple';

export const CustomSignUp: React.FC<CustomSignUpProps> = ({ onSignInClick }) => {
  const { signUp, setActive } = useSignUp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingVerification, setPendingVerification] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleOAuthSignUp = async (provider: OAuthProvider) => {
    if (!signUp) return;

    try {
      log.info(`starting OAuth sign-up with: ${provider}`);
      await signUp.authenticateWithRedirect({
        strategy: provider,
        redirectUrl: `${window.location.origin}/sso-callback`,
        redirectUrlComplete: `${window.location.origin}/`,
      });
    } catch (err: any) {
      log.error('OAuth sign-up error', err);
      setError(err.errors?.[0]?.longMessage || `Failed to sign up with ${provider}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUp) return;

    if (!acceptedTerms) {
      setError('Please accept the Terms of Use and EULA');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await signUp.create({
        emailAddress: email,
        password,
        firstName,
        lastName,
      });

      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setPendingVerification(true);
    } catch (err: any) {
      setError(err.errors?.[0]?.longMessage || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUp) return;

    setLoading(true);
    setError('');

    try {
      const result = await signUp.attemptEmailAddressVerification({
        code: verificationCode,
      });

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
      }
    } catch (err: any) {
      setError(err.errors?.[0]?.longMessage || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!signUp) return;
    setLoading(true);
    setError('');
    try {
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      log.info('verification code resent');
    } catch (err: any) {
      setError(err.errors?.[0]?.longMessage || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  if (pendingVerification) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.eyebrow}>Interlucent</div>
            <h1 className={styles.title}>VERIFY EMAIL</h1>
            <p className={styles.subtitle}>
              Enter the verification code sent to {email}
            </p>
          </div>

          <form onSubmit={handleVerify} className={styles.form}>
            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.field}>
              <label className={styles.label}>VERIFICATION CODE</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className={styles.input}
                required
                autoFocus
                placeholder="000000"
                autoComplete="one-time-code"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={styles.button}
            >
              {loading ? 'VERIFYING...' : 'VERIFY'}
            </button>

            <div className={styles.footer}>
              <span>Didn't receive a code?</span>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={loading}
                className={styles.linkButton}
              >
                Resend code
              </button>
            </div>

            <button
              type="button"
              onClick={() => setPendingVerification(false)}
              className={styles.linkButton}
            >
              Back
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.eyebrow}>Interlucent</div>
          <h1 className={styles.title}>CREATE ACCOUNT</h1>
          <p className={styles.subtitle}>
            Join Interlucent Console
          </p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.error}>{error}</div>}

          {/* SSO Buttons */}
          <div className={styles.ssoButtons}>
            <button
              type="button"
              onClick={() => handleOAuthSignUp('oauth_google')}
              className={styles.ssoButton}
            >
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707 0-.593.102-1.17.282-1.709V4.958H.957C.347 6.173 0 7.548 0 9c0 1.452.348 2.827.957 4.042l3.007-2.335z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
              </svg>
              <span>Continue with Google</span>
            </button>

            <button
              type="button"
              onClick={() => handleOAuthSignUp('oauth_github')}
              className={styles.ssoButton}
            >
              <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/>
              </svg>
              <span>Continue with GitHub</span>
            </button>

            {/* Apple SSO - disabled until Apple Developer account is configured
            <button
              type="button"
              onClick={() => handleOAuthSignUp('oauth_apple')}
              className={styles.ssoButton}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              <span>Continue with Apple</span>
            </button>
            */}
          </div>

          <div className={styles.divider}>
            <span>OR</span>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.label}>FIRST NAME</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={styles.input}
                required
                autoFocus
                autoComplete="given-name"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>LAST NAME</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={styles.input}
                required
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>EMAIL ADDRESS</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
              required
              autoComplete="email"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>PASSWORD</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              required
              minLength={8}
              autoComplete="new-password"
            />
            <p className={styles.hint}>Minimum 8 characters</p>
          </div>

          <div className={styles.checkbox}>
            <input
              type="checkbox"
              id="accept-terms"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className={styles.checkboxInput}
            />
            <label htmlFor="accept-terms" className={styles.checkboxLabel}>
              I accept the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className={styles.link}>
                Terms of Use
              </a>
              {' '}and{' '}
              <a href="/eula" target="_blank" rel="noopener noreferrer" className={styles.link}>
                EULA
              </a>
            </label>
          </div>

          <div id="clerk-captcha" />

          <button
            type="submit"
            disabled={loading || !acceptedTerms}
            className={styles.button}
          >
            {loading ? 'CREATING ACCOUNT...' : 'CREATE ACCOUNT'}
          </button>

          <div className={styles.footer}>
            <span>Already have an account?</span>
            <button
              type="button"
              onClick={onSignInClick}
              className={styles.linkButton}
            >
              Sign in
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
